import type React from 'react';

import Button from '@mui/material/Button';
import { useEffect, useRef } from 'react';
import {
  isRouteErrorResponse,
  Navigate,
  useLocation,
  useNavigate,
  useRouteError,
} from 'react-router';

import { useApiClient } from '@/api/util/useApiClient.util.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { isDynamicImportLoadError } from '@/errorHandling/dynamicImportError.util.ts';
import { buildErrorStackTrace } from '@/errorHandling/errorStackTrace.util.ts';
import { RouteEnum } from '@/layouts/routes/Route.model.ts';
import { recallComponentStack } from '@/monitoring/routerError.util.ts';
import { reloadPage } from '@/utils/reloadPage.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { GeneralError } from '@/views/generalError/GeneralError.view.tsx';

/**
 * `errorElement` of the pathless route wrapping all view routes (OCD-304).
 * Renders inside the root layout, so the app skeleton (sidebar/topbar)
 * survives a view render failure. Navigation from this screen is fully
 * client-side (no page reload). Router 404s are routed to the canonical
 * not-found screen separately from generic crash errors.
 *
 * Crash errors are also reported to the backend with the same stack trace
 * the previous in-layout boundary sent (error stack + component stack from
 * `RouterProvider`'s `onError`). Stale dynamic-import failures reload the
 * page after the report, like the boundary did before.
 */
export const RouteErrorView: React.FC = () => {
  const error = useRouteError();
  const location = useLocation();
  const navigate = useNavigate();
  const { frontendErrorsApi } = useApiClient();
  const { user } = useUserContext();
  const reportedErrorRef = useRef<unknown>(null);
  const { t } = useTranslationWithPrefix('generalError');
  const { t: tFallback } = useTranslationWithPrefix('components.fallbackUi');

  useEffect(() => {
    if (reportedErrorRef.current === error) {
      return;
    }
    if (isRouteErrorResponse(error) && error.status === 404) {
      return;
    }
    reportedErrorRef.current = error;

    const stackTrace = buildErrorStackTrace(error, recallComponentStack(error));

    const report: Promise<unknown> =
      user.userId && user.email
        ? frontendErrorsApi.reportFrontendError({
            email: user.email,
            stackTrace,
            userId: user.userId,
          })
        : Promise.resolve();

    if (import.meta.env.VITE_ENVIRONMENT !== 'local' && isDynamicImportLoadError(error)) {
      void report.catch(() => undefined).finally(() => reloadPage());
    } else {
      void report.catch(() => undefined);
    }
    // Report once per distinct error (identity guard covers StrictMode
    // double-run; deps re-report when another crash reaches this screen).
    // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
  }, [error]);

  if (isRouteErrorResponse(error) && error.status === 404) {
    if (location.pathname === `${RouteEnum.NOT_FOUND}`) {
      return <GeneralError message={t('notFound.message')} />;
    }
    return <Navigate replace to={RouteEnum.NOT_FOUND} />;
  }

  return (
    <GeneralError
      actions={
        <>
          <Button
            data-testid="route-error-go-back"
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
            variant="outlined"
          >
            {t('goBack')}
          </Button>
          <Button
            data-testid="route-error-start-page"
            onClick={() => navigate(RouteEnum.ROOT)}
            variant="outlined"
          >
            {t('startPage')}
          </Button>
        </>
      }
      message={tFallback('message')}
    />
  );
};
